import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getElectricityCost,
  getImportConsumption,
  getElectricityKwh,
  getPulseConsumption,
  getGasCost,
  getWaterCost,
  getAirQuality,
  getVibration,
  getAlertStatuses,
  type CostDataPoint,
  type ConsumptionDataPoint,
  type AirQualityDataPoint,
  type VibrationDataPoint,
  type DashboardConfig,
} from '../services/api';
import { getDateRange, getPrevDateRange, formatBucketLabel, prevPeriodLabel } from '../utils/dateUtils';
import type { Period, EnergySeries, KpiData, AqiSummary, AlertItem, ComparisonItem, VibrationSummary, VibrationClass } from '../components/dashboard/types';

export const PERIODS: Period[] = ['Today', 'This week', 'This month', 'This year'];

// Daily Air Quality Index (DAQI) scale, 1–10.
// https://uk-air.defra.gov.uk/air-pollution/daqi
// Overall DAQI is the max across all available particulates.

const PM25_BREAKPOINTS = [
  { daqi: 1,  max: 11  },
  { daqi: 2,  max: 23  },
  { daqi: 3,  max: 35  },
  { daqi: 4,  max: 41  },
  { daqi: 5,  max: 47  },
  { daqi: 6,  max: 53  },
  { daqi: 7,  max: 58  },
  { daqi: 8,  max: 64  },
  { daqi: 9,  max: 70  },
  { daqi: 10, max: Infinity },
];

const PM10_BREAKPOINTS = [
  { daqi: 1,  max: 16  },
  { daqi: 2,  max: 33  },
  { daqi: 3,  max: 50  },
  { daqi: 4,  max: 58  },
  { daqi: 5,  max: 66  },
  { daqi: 6,  max: 75  },
  { daqi: 7,  max: 83  },
  { daqi: 8,  max: 91  },
  { daqi: 9,  max: 100 },
  { daqi: 10, max: Infinity },
];

function toDaqi(value: number, breakpoints: typeof PM25_BREAKPOINTS): number {
  return breakpoints.find(b => value <= b.max)?.daqi ?? 10;
}

function daqiMeta(daqi: number): { band: string; color: string } {
  if (daqi <= 3)  return { band: 'Low',       color: '#1D9E75' };
  if (daqi <= 6)  return { band: 'Moderate',  color: '#BA7517' };
  if (daqi <= 9)  return { band: 'High',      color: '#D4537E' };
  return                 { band: 'Very High', color: '#993C1D' };
}

function buildAqiSummary(points: AirQualityDataPoint[]): AqiSummary {
  if (!points.length) {
    return {
      aqi: 0, status: 'No data', statusColor: '#888780',
      particulates: [],
    };
  }

  const latest = points[points.length - 1];

  const daqiPm25 = toDaqi(latest.pm25, PM25_BREAKPOINTS);
  const daqiPm10 = toDaqi(latest.pm10, PM10_BREAKPOINTS);
  const daqi     = Math.max(daqiPm25, daqiPm10);

  const { band, color: statusColor } = daqiMeta(daqi);

  return {
    aqi:         daqi,
    status:      `${band} (DAQI ${daqi})`,
    statusColor,
    particulates: [
      {
        name:  'PM2.5',
        value: Math.round(latest.pm25 * 10) / 10,
        unit:  'µg/m³',
        pct:   Math.min(Math.round(latest.pm25 / 70  * 100), 100),
        color: daqiMeta(daqiPm25).color,
      },
      {
        name:  'PM10',
        value: Math.round(latest.pm10 * 10) / 10,
        unit:  'µg/m³',
        pct:   Math.min(Math.round(latest.pm10 / 100 * 100), 100),
        color: daqiMeta(daqiPm10).color,
      },
      {
        name:  'PM1',
        value: Math.round(latest.pm1 * 10) / 10,
        unit:  'µg/m³',
        pct:   Math.min(Math.round(latest.pm1  / 30  * 100), 100),
        color: '#888780',
      },
      {
        name:  'PM4',
        value: Math.round(latest.pm4 * 10) / 10,
        unit:  'µg/m³',
        pct:   Math.min(Math.round(latest.pm4  / 50  * 100), 100),
        color: '#888780',
      },
    ],
  };
}

function buildEnergySeries(
  elecConsumption:  ConsumptionDataPoint[],
  gasConsumption:   ConsumptionDataPoint[],
  waterConsumption: ConsumptionDataPoint[],
  elecCost:         CostDataPoint[],
  period:           Period,
): EnergySeries {
  const labels      = elecConsumption.map(p => formatBucketLabel(p.bucket, period));
  const buckets     = elecConsumption.map(p => p.bucket);
  const electricity = elecConsumption.map(p => Math.round(p.consumption * 10) / 10);

  const floorToHour = (b: string) => {
    const d = new Date(b);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 13);
  };

  const gasByBucket   = new Map(gasConsumption.map(p   => [floorToHour(p.bucket), Math.round(p.consumption * 10) / 10]));
  const waterByBucket = new Map(waterConsumption.map(p  => [floorToHour(p.bucket), Math.round(p.consumption * 10) / 10]));
  const costByBucket  = new Map(elecCost.map(p          => [floorToHour(p.bucket), Math.round(p.cost_gbp   * 100) / 100]));

  const gas   = elecConsumption.map(p => gasByBucket.get(floorToHour(p.bucket))   ?? null);
  const water = elecConsumption.map(p => waterByBucket.get(floorToHour(p.bucket)) ?? null);
  const cost  = elecConsumption.map(p => costByBucket.get(floorToHour(p.bucket))  ?? null);

  return { labels, buckets, electricity, gas, water, cost };
}

function totalCost(points: CostDataPoint[]): number {
  return Math.round(points.reduce((s, p) => s + p.cost_gbp, 0) * 100) / 100;
}

function totalConsumption(points: ConsumptionDataPoint[]): number {
  return Math.round(points.reduce((s, p) => s + p.consumption, 0) * 10) / 10;
}

function vibrationStatus(value: number): { status: VibrationClass; color: string } {
  if (value <  2.8) return { status: 'Good',           color: '#1D9E75' };
  if (value <  7.1) return { status: 'Satisfactory',   color: '#BA7517' };
  if (value < 18.0) return { status: 'Unsatisfactory', color: '#D4537E' };
  return               { status: 'Unacceptable',    color: '#993C1D' };
}

function buildVibrationSummary(points: VibrationDataPoint[], period: Period): VibrationSummary {
  if (!points.length) {
    return {
      hasData: false,
      gauges:  [],
      series:  { labels: [], buckets: [], zrv1: [], xrv1: [], zpv1: [], xpv1: [] },
    };
  }

  const latest  = points[points.length - 1];
  const rnd     = (v: number) => Math.round(v * 100) / 100;

  const overallRms  = rnd((latest.zrv1 + latest.xrv1) / 2);
  const peakVel     = rnd(Math.max(latest.zpv1, latest.xpv1));

  const rmsStatus  = vibrationStatus(overallRms);
  const peakStatus = vibrationStatus(peakVel);

  const gauges = [
    {
      label:       'Overall RMS',
      value:       overallRms,
      max:         20,
      status:      rmsStatus.status,
      statusColor: rmsStatus.color,
    },
    {
      label:       'Peak Velocity',
      value:       peakVel,
      max:         20,
      status:      peakStatus.status,
      statusColor: peakStatus.color,
    },
  ];

  const labels  = points.map(p => formatBucketLabel(p.bucket, period));
  const buckets = points.map(p => p.bucket);

  return {
    hasData: true,
    gauges,
    series: {
      labels,
      buckets,
      zrv1: points.map(p => rnd(p.zrv1)),
      xrv1: points.map(p => rnd(p.xrv1)),
      zpv1: points.map(p => rnd(p.zpv1)),
      xpv1: points.map(p => rnd(p.xpv1)),
    },
  };
}

function mapAlerts(rawAlerts: any[]): AlertItem[] {
  return rawAlerts.map(a => {
    const isBreaching = !!a.alert_state?.breaching_since;
    const isSilenced  = a.silenced_until && new Date(a.silenced_until) > new Date();
    const type        = !a.is_active || isSilenced ? 'ok'
                      : isBreaching                ? 'warn'
                      :                              'info';

    const breachingSince = a.alert_state?.breaching_since
      ? new Date(a.alert_state.breaching_since).toLocaleString('en-GB', {
          hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short',
        })
      : null;

    const message = isBreaching
      ? `${a.name} — breaching`
      : isSilenced
      ? `${a.name} — silenced until ${new Date(a.silenced_until!).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
      : `${a.name} — dormant`;

    return {
      id:      a.id,
      type,
      message,
      time:    breachingSince ?? 'active',
    };
  });
}

export interface DashboardData {
  kpis:            KpiData[];
  series:          EnergySeries;
  costBreakdown:   { label: string; value: number; color: string }[];
  airQuality:      AqiSummary;
  vibration:       VibrationSummary;
  alerts:          AlertItem[];
  comparison:      ComparisonItem[];
  prevPeriodLabel: string;
}

interface UseDashboardOptions {
  osmName:    string;
  config:     DashboardConfig | null;
  refreshMs?: number;
}

export function useDashboard({
  osmName,
  config,
  refreshMs = 60_000,
}: UseDashboardOptions) {
  const [period, setPeriod] = useState<Period>('This week');
  const [data,   setData]   = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const fetchAll = useCallback(async () => {
    if (!osmName) return;

    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setError(null);

    const { from, to }     = getDateRange(period);
    const { from: prevFrom,
            to:   prevTo } = getPrevDateRange(period);
    const prevLabel        = prevPeriodLabel(period);

    try {
      const [
        elecCost,
        elecConsumptionRaw,
        airQualityPoints,
        vibrationPoints,
        rawAlerts,
        gasCostPoints,
        gasConsumption,
        waterCostPoints,
        waterConsumption,
        prevElecCost,
        prevElecConsumptionRaw,
        prevGasCostPoints,
        prevGasConsumption,
        prevWaterCostPoints,
        prevWaterConsumption,
      ] = await Promise.all([
        getElectricityCost(osmName, from, to).catch(() => [] as CostDataPoint[]),
        getImportConsumption(osmName, from, to).catch(() => [] as ConsumptionDataPoint[]),
        getAirQuality(osmName, from, to).catch(() => [] as AirQualityDataPoint[]),
        getVibration(osmName, from, to).catch(() => [] as VibrationDataPoint[]),
        getAlertStatuses().catch(() => []),
        config?.gas_channel
          ? getGasCost(osmName, from, to, config.gas_channel, config.gas_pulse_div ?? 1).catch(() => [] as CostDataPoint[])
          : Promise.resolve<CostDataPoint[]>([]),
        config?.gas_channel
          ? getPulseConsumption(osmName, from, to, config.gas_channel, config.gas_pulse_div ?? 1).catch(() => [] as ConsumptionDataPoint[])
          : Promise.resolve<ConsumptionDataPoint[]>([]),
        config?.water_channel
          ? getWaterCost(osmName, from, to, config.water_channel, config.water_pulse_div ?? 1).catch(() => [] as CostDataPoint[])
          : Promise.resolve<CostDataPoint[]>([]),
        config?.water_channel
          ? getPulseConsumption(osmName, from, to, config.water_channel, config.water_pulse_div ?? 1).catch(() => [] as ConsumptionDataPoint[])
          : Promise.resolve<ConsumptionDataPoint[]>([]),
        getElectricityCost(osmName, prevFrom, prevTo).catch(() => [] as CostDataPoint[]),
        getImportConsumption(osmName, prevFrom, prevTo).catch(() => [] as ConsumptionDataPoint[]),
        config?.gas_channel
          ? getGasCost(osmName, prevFrom, prevTo, config.gas_channel, config.gas_pulse_div ?? 1).catch(() => [] as CostDataPoint[])
          : Promise.resolve<CostDataPoint[]>([]),
        config?.gas_channel
          ? getPulseConsumption(osmName, prevFrom, prevTo, config.gas_channel, config.gas_pulse_div ?? 1).catch(() => [] as ConsumptionDataPoint[])
          : Promise.resolve<ConsumptionDataPoint[]>([]),
        config?.water_channel
          ? getWaterCost(osmName, prevFrom, prevTo, config.water_channel, config.water_pulse_div ?? 1).catch(() => [] as CostDataPoint[])
          : Promise.resolve<CostDataPoint[]>([]),
        config?.water_channel
          ? getPulseConsumption(osmName, prevFrom, prevTo, config.water_channel, config.water_pulse_div ?? 1).catch(() => [] as ConsumptionDataPoint[])
          : Promise.resolve<ConsumptionDataPoint[]>([]),
      ]);

      // Fall back to getElectricityKwh if the sensor doesn't support import consumption
      const elecConsumption = elecConsumptionRaw.length > 0
        ? elecConsumptionRaw
        : (await getElectricityKwh(osmName, from, to).catch(() => [])).map((p: any) => ({
            bucket:      p.bucket,
            consumption: p.kwh ?? p.consumption ?? 0,
          }));

      console.log('import consumption length:', elecConsumptionRaw.length);
      console.log('using fallback:', elecConsumptionRaw.length === 0);

      const prevElecConsumption = prevElecConsumptionRaw.length > 0
        ? prevElecConsumptionRaw
        : (await getElectricityKwh(osmName, prevFrom, prevTo).catch(() => [])).map((p: any) => ({
            bucket:      p.bucket,
            consumption: p.kwh ?? p.consumption ?? 0,
          }));

      const elecTotal      = totalCost(elecCost);
      const gasTotal       = totalCost(gasCostPoints);
      const waterTotal     = totalCost(waterCostPoints);
      const grandTotal     = Math.round((elecTotal + gasTotal + waterTotal) * 100) / 100;
      const prevElecTotal  = totalCost(prevElecCost);
      const prevGasTotal   = totalCost(prevGasCostPoints);
      const prevWaterTotal = totalCost(prevWaterCostPoints);
      const prevGrandTotal = Math.round((prevElecTotal + prevGasTotal + prevWaterTotal) * 100) / 100;
      const aqiSummary     = buildAqiSummary(airQualityPoints);
      const vibSummary     = buildVibrationSummary(vibrationPoints, period);

      const kpis: KpiData[] = [
        {
          id:       'electricity',
          value:    String(totalConsumption(elecConsumption)),
          subLabel: 'kWh used',
        },
        {
          id:       'gas',
          value:    String(totalConsumption(gasConsumption)),
          subLabel: 'm³ consumed',
        },
        {
          id:       'water',
          value:    String(totalConsumption(waterConsumption)),
          subLabel: 'm³ consumed',
        },
        {
          id:       'cost',
          value:    `£${grandTotal}`,
          subLabel: `total ${period.toLowerCase()}`,
        },
      ];

      const costBreakdown = [
        { label: 'Electricity', value: elecTotal,  color: '#1D9E75' },
        { label: 'Gas',         value: gasTotal,   color: '#BA7517' },
        { label: 'Water',       value: waterTotal, color: '#378ADD' },
      ].filter(d => d.value > 0);

      const comparison: ComparisonItem[] = [
        {
          id:       'electricity',
          label:    'Electricity',
          current:  totalConsumption(elecConsumption),
          previous: totalConsumption(prevElecConsumption),
          unit:     'kWh',
          color:    '#1D9E75',
        },
        ...(config?.gas_channel ? [{
          id:       'gas',
          label:    'Gas',
          current:  totalConsumption(gasConsumption),
          previous: totalConsumption(prevGasConsumption),
          unit:     'm³',
          color:    '#BA7517',
        }] : []),
        ...(config?.water_channel ? [{
          id:       'water',
          label:    'Water',
          current:  totalConsumption(waterConsumption),
          previous: totalConsumption(prevWaterConsumption),
          unit:     'm³',
          color:    '#378ADD',
        }] : []),
        {
          id:       'cost',
          label:    'Total cost',
          current:  grandTotal,
          previous: prevGrandTotal,
          unit:     '',
          prefix:   '£',
          color:    '#D4537E',
        },
      ];

      setData({
        kpis,
        series:          buildEnergySeries(elecConsumption, gasConsumption, waterConsumption, elecCost, period),
        costBreakdown,
        airQuality:      aqiSummary,
        vibration:       vibSummary,
        alerts:          mapAlerts(rawAlerts),
        comparison,
        prevPeriodLabel: prevLabel,
      });
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('[useDashboard]', err);
        setError(err.message ?? 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  }, [osmName, config, period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!refreshMs) return;
    const id = setInterval(fetchAll, refreshMs);
    return () => clearInterval(id);
  }, [fetchAll, refreshMs]);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  return { period, setPeriod, periods: PERIODS, data, loading, error, refetch: fetchAll };
}

